import { Button } from '@heidi/design-system/button';
import { useAtomValue } from 'jotai';
import { Sparkles } from 'lucide-react';
import { type ComponentProps, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useIsTemplateComplianceFixForMeEnabled } from '../../../flags';
import { regulatoryGuidelineComplianceReasonAtom } from '../../template-builder/atoms';
import { buildRegulatoryComplianceFixPrompt } from '../regulatoryComplianceFix';
import { useHandleSend } from '../tabs/chatbox/actionButtons/hooks/useHandleSend';
import { templateDetailsMessages } from '../templateDetails.messages';

/** Renders on the Flagsmith rollout flag plus `isOpen`; never re-reads the PostHog compliance keys. */
export const RegulatoryComplianceFixButton = ({
  isOpen,
  onFixed,
  size = 'lg',
}: {
  isOpen: boolean;
  onFixed: () => void;
  size?: ComponentProps<typeof Button>['size'];
}) => {
  const complianceReason = useAtomValue(regulatoryGuidelineComplianceReasonAtom);
  const { handleSend, shouldDisableTemplateEditor } = useHandleSend({ editor: null });
  const isTemplateComplianceFixForMeEnabled = useIsTemplateComplianceFixForMeEnabled();
  const canFixForMe = isOpen && isTemplateComplianceFixForMeEnabled;

  const handleFixForMe = useCallback(() => {
    void handleSend({
      markdownContent: buildRegulatoryComplianceFixPrompt(complianceReason),
      verifyCompliance: true,
      forceStructure: true,
    });
    onFixed();
  }, [complianceReason, handleSend, onFixed]);

  if (!canFixForMe) {
    return null;
  }

  return (
    <Button size={size} onClick={handleFixForMe} disabled={shouldDisableTemplateEditor}>
      <Sparkles />
      <FormattedMessage {...templateDetailsMessages.fixForMe} />
    </Button>
  );
};
